/**
 * Browser-side client for the per-KB taxonomy + LLM-extraction APIs
 * exposed by `knowledge-service` (proxied through `api-gateway`).
 *
 * Why a dedicated module rather than inlining `api(…)` calls in each
 * component:
 *
 * - The route surface is large (7 taxonomy resources × CRUD + 6
 *   extraction endpoints) and the URL templates would otherwise leak
 *   into half a dozen components, drifting independently.
 * - All endpoints are tenant-scoped on the gateway side; nothing here
 *   needs `team_id` plumbing — but keeping the client thin makes that
 *   invariant easy to audit.
 * - Tests mock `api`/`apiRaw` at the network boundary; layering a
 *   semantic client on top means they only need to assert which
 *   *function* a component called, not the URL template.
 *
 * See:
 *   - `backend/services/knowledge-service/src/kos_knowledge_svc/api/taxonomy.py`
 *   - `backend/services/knowledge-service/src/kos_knowledge_svc/api/extraction.py`
 */

import { api } from "@/lib/api";
import type {
  CandidateKind,
  CandidateStatus,
  ExtractorKind,
  KbAliasView,
  KbEntityCandidateView,
  KbEntityCascadeResponse,
  KbEntityRelationView,
  KbEntityTypeView,
  KbEntityView,
  KbExtractionRunView,
  KbIdentifierView,
  KbMetricFactView,
  KbPredicateCandidateView,
  KbPredicateView,
  KbPreviewRequest,
  KbPreviewResponse,
  KbTaxonomyBundle,
  KbTaxonomyDeleteResponse,
  KbTopicCandidateView,
  KbTopicCascadeResponse,
  KbTopicRelationView,
  KbTopicView,
  PromoteEntityRequest,
  PromotePredicateRequest,
  PromoteTopicRequest,
  RejectCandidateRequest,
  UpsertKbAliasRequest,
  UpsertKbEntityRelationRequest,
  UpsertKbEntityRequest,
  UpsertKbEntityTypeRequest,
  UpsertKbIdentifierRequest,
  UpsertKbTopicRelationRequest,
  UpsertKbTopicRequest,
} from "@/lib/types";

// ────────────────────────────────────────────────────────────────────
// Taxonomy
// ────────────────────────────────────────────────────────────────────

const taxRoot = (kbId: string): string => `/kbs/${kbId}/taxonomy`;

/** Snapshot read used for first paint and refresh. */
export function getTaxonomy(kbId: string): Promise<KbTaxonomyBundle> {
  return api<KbTaxonomyBundle>(taxRoot(kbId));
}

export function upsertTopic(kbId: string, body: UpsertKbTopicRequest): Promise<KbTopicView> {
  return api<KbTopicView>(`${taxRoot(kbId)}/topics`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function deleteTopic(kbId: string, topicId: string): Promise<KbTopicCascadeResponse> {
  return api<KbTopicCascadeResponse>(`${taxRoot(kbId)}/topics/${topicId}`, {
    method: "DELETE",
  });
}

export function upsertEntityType(
  kbId: string,
  body: UpsertKbEntityTypeRequest,
): Promise<KbEntityTypeView> {
  return api<KbEntityTypeView>(`${taxRoot(kbId)}/entity-types`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function deleteEntityType(
  kbId: string,
  entityTypeId: string,
): Promise<KbTaxonomyDeleteResponse> {
  return api<KbTaxonomyDeleteResponse>(`${taxRoot(kbId)}/entity-types/${entityTypeId}`, {
    method: "DELETE",
  });
}

export function upsertEntity(kbId: string, body: UpsertKbEntityRequest): Promise<KbEntityView> {
  return api<KbEntityView>(`${taxRoot(kbId)}/entities`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function deleteEntity(kbId: string, entityId: string): Promise<KbEntityCascadeResponse> {
  return api<KbEntityCascadeResponse>(`${taxRoot(kbId)}/entities/${entityId}`, {
    method: "DELETE",
  });
}

export function upsertAlias(kbId: string, body: UpsertKbAliasRequest): Promise<KbAliasView> {
  return api<KbAliasView>(`${taxRoot(kbId)}/aliases`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function deleteAlias(kbId: string, aliasId: string): Promise<KbTaxonomyDeleteResponse> {
  return api<KbTaxonomyDeleteResponse>(`${taxRoot(kbId)}/aliases/${aliasId}`, {
    method: "DELETE",
  });
}

export function upsertIdentifier(
  kbId: string,
  body: UpsertKbIdentifierRequest,
): Promise<KbIdentifierView> {
  return api<KbIdentifierView>(`${taxRoot(kbId)}/identifiers`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function deleteIdentifier(
  kbId: string,
  identifierId: string,
): Promise<KbTaxonomyDeleteResponse> {
  return api<KbTaxonomyDeleteResponse>(`${taxRoot(kbId)}/identifiers/${identifierId}`, {
    method: "DELETE",
  });
}

export function upsertTopicRelation(
  kbId: string,
  body: UpsertKbTopicRelationRequest,
): Promise<KbTopicRelationView> {
  return api<KbTopicRelationView>(`${taxRoot(kbId)}/topic-relations`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function deleteTopicRelation(
  kbId: string,
  relationId: string,
): Promise<KbTaxonomyDeleteResponse> {
  return api<KbTaxonomyDeleteResponse>(`${taxRoot(kbId)}/topic-relations/${relationId}`, {
    method: "DELETE",
  });
}

export function upsertEntityRelation(
  kbId: string,
  body: UpsertKbEntityRelationRequest,
): Promise<KbEntityRelationView> {
  return api<KbEntityRelationView>(`${taxRoot(kbId)}/entity-relations`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function deleteEntityRelation(
  kbId: string,
  relationId: string,
): Promise<KbTaxonomyDeleteResponse> {
  return api<KbTaxonomyDeleteResponse>(`${taxRoot(kbId)}/entity-relations/${relationId}`, {
    method: "DELETE",
  });
}

// ────────────────────────────────────────────────────────────────────
// Extraction — runs, candidates, facts, preview
// ────────────────────────────────────────────────────────────────────

const extRoot = (kbId: string): string => `/kbs/${kbId}/extraction`;

export function listPredicates(kbId: string): Promise<KbPredicateView[]> {
  return api<KbPredicateView[]>(`${extRoot(kbId)}/predicates`);
}

export interface ListRunsParams {
  /** Either `version_id` or `document_id` is required (the panel uses
   *  whichever it has handy; the API enforces "exactly one"). */
  version_id?: string;
  document_id?: string;
  status?: string;
  extractor?: ExtractorKind | string;
  limit?: number;
}

export function listExtractionRuns(
  kbId: string,
  params: ListRunsParams,
): Promise<KbExtractionRunView[]> {
  const search = new URLSearchParams();
  if (params.version_id) search.set("version_id", params.version_id);
  if (params.document_id) search.set("document_id", params.document_id);
  if (params.status) search.set("status", params.status);
  if (params.extractor) search.set("extractor", params.extractor);
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  const qs = search.toString();
  return api<KbExtractionRunView[]>(`${extRoot(kbId)}/runs${qs ? `?${qs}` : ""}`);
}

export interface ListCandidatesParams {
  /** Defaults to `pending` server-side; pass `null` to leave the param
   *  out and accept the server default. */
  status?: CandidateStatus;
  limit?: number;
}

function candidateQs(params: ListCandidatesParams): string {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function listTopicCandidates(
  kbId: string,
  params: ListCandidatesParams = {},
): Promise<KbTopicCandidateView[]> {
  return api<KbTopicCandidateView[]>(`${extRoot(kbId)}/candidates/topics${candidateQs(params)}`);
}

export function listPredicateCandidates(
  kbId: string,
  params: ListCandidatesParams = {},
): Promise<KbPredicateCandidateView[]> {
  return api<KbPredicateCandidateView[]>(
    `${extRoot(kbId)}/candidates/predicates${candidateQs(params)}`,
  );
}

export function listEntityCandidates(
  kbId: string,
  params: ListCandidatesParams = {},
): Promise<KbEntityCandidateView[]> {
  return api<KbEntityCandidateView[]>(`${extRoot(kbId)}/candidates/entities${candidateQs(params)}`);
}

/** Promote a topic candidate; payload requires the catalog `slug`. */
export function promoteTopicCandidate(
  kbId: string,
  candidateId: string,
  body: PromoteTopicRequest,
): Promise<KbTopicCandidateView> {
  return api<KbTopicCandidateView>(`${extRoot(kbId)}/candidates/topics/${candidateId}/promote`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Promote a predicate candidate; payload requires the catalog `slug`. */
export function promotePredicateCandidate(
  kbId: string,
  candidateId: string,
  body: PromotePredicateRequest,
): Promise<KbPredicateCandidateView> {
  return api<KbPredicateCandidateView>(
    `${extRoot(kbId)}/candidates/predicates/${candidateId}/promote`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

/** Promote an entity candidate; payload requires `entity_type_id`. */
export function promoteEntityCandidate(
  kbId: string,
  candidateId: string,
  body: PromoteEntityRequest,
): Promise<KbEntityCandidateView> {
  return api<KbEntityCandidateView>(`${extRoot(kbId)}/candidates/entities/${candidateId}/promote`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Generic reject — uses the same payload across all three kinds.
 *  We keep three separate functions (rather than dispatching on
 *  ``kind``) so the return type stays narrow. */
export function rejectTopicCandidate(
  kbId: string,
  candidateId: string,
  body: RejectCandidateRequest = {},
): Promise<KbTopicCandidateView> {
  return api<KbTopicCandidateView>(`${extRoot(kbId)}/candidates/topics/${candidateId}/reject`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function rejectPredicateCandidate(
  kbId: string,
  candidateId: string,
  body: RejectCandidateRequest = {},
): Promise<KbPredicateCandidateView> {
  return api<KbPredicateCandidateView>(
    `${extRoot(kbId)}/candidates/predicates/${candidateId}/reject`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export function rejectEntityCandidate(
  kbId: string,
  candidateId: string,
  body: RejectCandidateRequest = {},
): Promise<KbEntityCandidateView> {
  return api<KbEntityCandidateView>(`${extRoot(kbId)}/candidates/entities/${candidateId}/reject`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface ListFactsParams {
  /** Either `subject_entity_id` or `subject_text` is required (the
   *  server rejects unfiltered scans). */
  subject_entity_id?: string;
  subject_text?: string;
  metric_slug?: string;
  limit?: number;
}

export function listFacts(kbId: string, params: ListFactsParams): Promise<KbMetricFactView[]> {
  const search = new URLSearchParams();
  if (params.subject_entity_id) search.set("subject_entity_id", params.subject_entity_id);
  if (params.subject_text) search.set("subject_text", params.subject_text);
  if (params.metric_slug) search.set("metric_slug", params.metric_slug);
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  const qs = search.toString();
  return api<KbMetricFactView[]>(`${extRoot(kbId)}/facts${qs ? `?${qs}` : ""}`);
}

/** Render the (system, user) prompt the LLM would see — pure prompt
 *  builder, no LLM call, no run row written. */
export function previewPrompt(kbId: string, body: KbPreviewRequest): Promise<KbPreviewResponse> {
  return api<KbPreviewResponse>(`${extRoot(kbId)}/preview`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** A 5-tuple kind used by the candidate-row UI when we need to
 *  promote / reject without re-fetching the whole queue. Helps the
 *  `<CandidatesTab>` narrow on `kind` once and pick the right
 *  helper without a chain of `if` blocks at every call site. */
export type CandidateRow =
  | { kind: "topics"; row: KbTopicCandidateView }
  | { kind: "predicates"; row: KbPredicateCandidateView }
  | { kind: "entities"; row: KbEntityCandidateView };

export type AnyCandidateRow =
  | KbTopicCandidateView
  | KbPredicateCandidateView
  | KbEntityCandidateView;

export function candidateLabel(kind: CandidateKind, row: AnyCandidateRow): string {
  if (kind === "entities") return (row as KbEntityCandidateView).proposed_canonical_name;
  return (row as KbTopicCandidateView | KbPredicateCandidateView).proposed_label;
}
